import { compareAsc, differenceInMinutes } from 'date-fns';
import { toast } from 'react-toastify';
import { ClinicalItem } from '@features/components/ClinicalData/types';
import {
  ClinicalData,
  ClinicalItemType,
  ClinicalMediaFile,
  ClinicalMediaType,
  PerfusionTerminationType,
  TimestampWithZone,
} from '@/api/apis/case';
import {
  convertToIanaTimezone,
  formatDate,
  getClinicalRecordByType,
  TIMEZONE_MAP,
  TIME_WITH_TIMEZONE_FORMAT,
} from '@/utils/helpers';
import { getFileUrl } from '@/api/utils';

const formatOcsTime = (timestamp?: string, timezone?: string) => ({
  timestamp: timestamp || new Date().toISOString(),
  timezone: timezone || TIMEZONE_MAP.EST,
});

export const getPerfusionTime = (
  currentValueDateTime: Date | string | number,
  ocsStartDateTime: Date | string | number,
): string => {
  const minutesDiff = Math.max(0, differenceInMinutes(currentValueDateTime, ocsStartDateTime));
  const hours = Math.floor(minutesDiff / 60);
  const minutes = minutesDiff % 60;
  const mappedTimeValues = [hours, minutes].map((v) => v.toString().padStart(2, '0'));
  return mappedTimeValues.join(':');
};

export const getXsTime = (clinicalData: ClinicalData) => {
  const crossClampTime = getClinicalRecordByType(clinicalData, ClinicalItemType.CrossClampTimeFlushTime)?.item;
  if (!crossClampTime) {
    return null;
  }
  const { timestamp, timeZone } = crossClampTime;
  return formatOcsTime(timestamp, timeZone);
};

export const getOcsStart = (clinicalData: ClinicalData) => {
  const onOcsStart = getClinicalRecordByType(clinicalData, ClinicalItemType.OrganOnOCS)?.item;
  const { timestamp, timeZone } = onOcsStart ?? {};
  return formatOcsTime(timestamp, timeZone);
};

export const isClinicalRecordOffOcs = <T extends Omit<ClinicalItem, 'mediaFiles'>>(
  logRecord: T,
  orderedRecords: T[],
): boolean => {
  // For Lungs
  if (logRecord.clinicalItemType === ClinicalItemType.PerfusionTerminationOption) {
    if (logRecord.details === PerfusionTerminationType.doubleLungFlush) {
      return true;
    }

    if (logRecord.details === PerfusionTerminationType.secondLungFlush) {
      const firstLungFlushRecord = orderedRecords.find(
        (record) => record.details === PerfusionTerminationType.firstLungFlush,
      );
      return !!firstLungFlushRecord;
    }

    if (logRecord.details === PerfusionTerminationType.firstLungFlush) {
      const secondLungFlushRecord = orderedRecords.find(
        (record) => record.details === PerfusionTerminationType.secondLungFlush,
      );
      return !!secondLungFlushRecord;
    }

    return false;
  }

  // For Heart and Liver
  return logRecord.clinicalItemType === ClinicalItemType.OffOCS;
};

export const getOffOcs = (clinicalData: ClinicalData): TimestampWithZone | null => {
  // For Heart and Liver
  const offOcs = getClinicalRecordByType(clinicalData, ClinicalItemType.OffOCS);
  if (offOcs?.item) {
    return offOcs.item;
  }

  // For Lungs
  const { sequentialLungFlush, doubleLungFlush } =
    getClinicalRecordByType(clinicalData, ClinicalItemType.PerfusionTerminationOption)?.item ?? {};
  let isFirstFlushLater;

  const [firstLungFlushTime, secondLungFlushTime] = [
    sequentialLungFlush?.firstLungFlushTime,
    sequentialLungFlush?.secondLungFlushTime,
  ];

  if (firstLungFlushTime && secondLungFlushTime) {
    isFirstFlushLater = compareAsc(new Date(firstLungFlushTime.timestamp), new Date(secondLungFlushTime.timestamp)) > 0;
  }

  const latestSequentialLungFlushTime = isFirstFlushLater
    ? sequentialLungFlush?.firstLungFlushTime
    : sequentialLungFlush?.secondLungFlushTime;

  return latestSequentialLungFlushTime || doubleLungFlush?.flushTime || null;
};

export const getOcsEnd = (clinicalData: ClinicalData) => {
  const offOcsTime = getOffOcs(clinicalData);
  const { timestamp, timeZone } = offOcsTime ?? {};
  return formatOcsTime(timestamp, timeZone);
};

export const getLungsRadiometer = (clinicalData: ClinicalData) => {
  const pfRatioInSitu = getClinicalRecordByType(clinicalData, ClinicalItemType.PFRatioInSitu)?.item;
  const donorHospitalPFRatio =
    getClinicalRecordByType(clinicalData, ClinicalItemType.PFRatioOnOCSDonorHospital)?.items || [];
  const txCenterPFRatio = getClinicalRecordByType(clinicalData, ClinicalItemType.PFRatioOnOCSTXCenter)?.items || [];
  const measurements = [];
  if (pfRatioInSitu) measurements.push(pfRatioInSitu);
  measurements.push(...donorHospitalPFRatio, ...txCenterPFRatio);
  return measurements;
};

export const getBloodSamplesRadiometer = (clinicalData: ClinicalData) => {
  const donorHospitalLactate = getClinicalRecordByType(clinicalData, ClinicalItemType.PreOCSBloodSample)?.items || [];
  const txCenterLactate = getClinicalRecordByType(clinicalData, ClinicalItemType.BloodSample)?.items || [];
  return [...donorHospitalLactate, ...txCenterLactate];
};

export const formatClinicalTime = (timestamp: string, timezone?: string) => {
  return formatDate(timestamp, TIME_WITH_TIMEZONE_FORMAT, { timezone: convertToIanaTimezone(timezone) });
};

export const convertMediaFiles = (
  files: (ClinicalMediaFile & Partial<TimestampWithZone>)[],
  timestamp: string,
  timeZone: string,
  ocsTime?: string,
): ClinicalMediaType[] => {
  return files.map((file) => ({ file, timestamp, timeZone, ocsTime }));
};

export const downloadMedia = (path: string, fileName: string, useDirectPath?: boolean) => {
  const url = useDirectPath ? path : getFileUrl(path);
  if (url) {
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', fileName || 'download');
    a.setAttribute('target', '_blank');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    toast.error('File URL is not valid.');
  }
};
